import {
  createCurrencyQuery,
  createChatQuery,
  createConfirmationQuery,
  createLocationQuery,
  createTransferQuery,
  createUsersQuery,
  createTravelAgentQuery,
  createTrackingQuery,
  createEngageQuery,
  createVoucherQuery,
  createAppleWalletQuery,
  createGoogleWalletQuery,
  createSearchQuery,
  AuthError,
} from "@mozioinc/query";
import { expireSession, refreshTokenSingleton } from "./auth";
import { getEnvs } from "./env";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "./token";
import { getInitialLocale } from "./translation";
import { getWhiteLabel } from "./white-label";

const getEffectivePartnerRef = () => {
  const refParam = new URLSearchParams(window.location.search).get("ref");
  const { partnerRef } = getWhiteLabel();
  return refParam || partnerRef;
};

const { API_ENV } = getEnvs();

export const locationQuery = createLocationQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    lang: getInitialLocale(),
  },
});

export const currencyQuery = createCurrencyQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    lang: getInitialLocale(true),
  },
}));

export const searchQuery = createSearchQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Accept-Version": "v2.1",
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
    "Content-Type": "application/json",
    Lang: getInitialLocale(),
  },
}));

// The voucher endpoint is public (accessed by hashed reservation id), so this
// query intentionally sends no api-key / authorization headers.
export const voucherQuery = createVoucherQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {},
});

export const appleWalletQuery = createAppleWalletQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
  },
});

export const googleWalletQuery = createGoogleWalletQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
  },
});

export const confirmationQuery = createConfirmationQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Lang: getInitialLocale(true),
  },
});

const refreshTokensRetryCounts = new Map<string, number>();
const MAX_RETRY_COUNT = 3;

export const transferQuery = createTransferQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
    "Content-Type": "application/json",
    Lang: getInitialLocale(),
  },
  postProcess: {
    onError: async ({ options, input, error, fetcher, fetcherName }) => {
      const isAuthError = error instanceof AuthError;
      if (!isAuthError) {
        throw error;
      }

      if ((refreshTokensRetryCounts.get(fetcherName) ?? 0) >= MAX_RETRY_COUNT) {
        console.error("Max retry count reached");
        throw expireSession();
      }

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 0) + 1,
      );

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.error("No refresh token found");
        throw expireSession();
      }

      const result = await refreshTokenSingleton(refreshToken);
      if ("error" in result) {
        console.error(result.message);
        throw expireSession();
      }

      setAccessToken(result.accessToken);
      setRefreshToken(result.refreshToken);
      const response = await fetcher(options, input as any); // eslint-disable-line @typescript-eslint/no-explicit-any

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 1) - 1,
      );

      return response;
    },
  },
}));

export const usersQuery = createUsersQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
    Lang: getInitialLocale(),
  },
  postProcess: {
    onError: async ({ options, error, input, fetcher, fetcherName }) => {
      const isAuthError = error instanceof AuthError;

      if (!isAuthError) {
        throw error;
      }

      if ((refreshTokensRetryCounts.get(fetcherName) ?? 0) >= MAX_RETRY_COUNT) {
        console.error("Max retry count reached");
        throw expireSession();
      }

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 0) + 1,
      );

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.error("No refresh token found");
        throw expireSession();
      }

      const result = await refreshTokenSingleton(refreshToken);
      if ("error" in result) {
        console.error(result.message);
        throw expireSession();
      }

      setAccessToken(result.accessToken);
      setRefreshToken(result.refreshToken);
      const response = await fetcher(options, input as any); // eslint-disable-line @typescript-eslint/no-explicit-any

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 1) - 1,
      );

      return response;
    },
  },
}));

export const travelAgentQuery = createTravelAgentQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
    Lang: getInitialLocale(),
  },
  postProcess: {
    onError: async ({ options, error, input, fetcher, fetcherName }) => {
      const isAuthError = error instanceof AuthError;

      if (!isAuthError) {
        throw error;
      }

      if ((refreshTokensRetryCounts.get(fetcherName) ?? 0) >= MAX_RETRY_COUNT) {
        console.error("Max retry count reached");
        throw expireSession();
      }

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 0) + 1,
      );

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.error("No refresh token found");
        throw expireSession();
      }

      const result = await refreshTokenSingleton(refreshToken);
      if ("error" in result) {
        console.error(result.message);
        throw expireSession();
      }

      setAccessToken(result.accessToken);
      setRefreshToken(result.refreshToken);
      const response = await fetcher(options, input as any); // eslint-disable-line @typescript-eslint/no-explicit-any

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 1) - 1,
      );

      return response;
    },
  },
}));

export const engageQuery = createEngageQuery({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Lang: getInitialLocale(),
  },
});

export const trackingQuery = createTrackingQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Lang: getInitialLocale(),
    ref: getEffectivePartnerRef(),
  },
}));

export const chatQuery = createChatQuery(() => ({
  ctx: {
    env: API_ENV,
  },
  headers: {
    "api-key": "c09cc3d01bbe22cb5d7afab4f82c8f3f",
    "Content-Type": "application/json",
    Authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
    Lang: getInitialLocale(),
  },
  postProcess: {
    onError: async ({ options, error, input, fetcher, fetcherName }) => {
      const isAuthError = error instanceof AuthError;

      if (!isAuthError) {
        throw error;
      }

      if ((refreshTokensRetryCounts.get(fetcherName) ?? 0) >= MAX_RETRY_COUNT) {
        console.error("Max retry count reached");
        throw expireSession();
      }

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 0) + 1,
      );

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.error("No refresh token found");
        throw expireSession();
      }

      const result = await refreshTokenSingleton(refreshToken);
      if ("error" in result) {
        console.error(result.message);
        throw expireSession();
      }

      setAccessToken(result.accessToken);
      setRefreshToken(result.refreshToken);
      const response = await fetcher(options, input as any); // eslint-disable-line @typescript-eslint/no-explicit-any

      refreshTokensRetryCounts.set(
        fetcherName,
        (refreshTokensRetryCounts.get(fetcherName) ?? 1) - 1,
      );

      return response;
    },
  },
}));
